import { ErrorComponent } from "@components/common/ErrorComponent";
import { NotFound } from "@components/common/NotFound";
import { RootLayout } from "@components/layout/RootLayout";
import { useAuthStore } from "@lib/auth/authStore";
import {
  deriveCapabilities,
  determineUserType,
  getDashboardRoute,
} from "@lib/permissions/capabilities";
import { permissionQueryOptions } from "@lib/queries/permissionQueries";
import { currentUserQueryOptions } from "@lib/queries/userQueries";
import { usePermissionStore } from "@lib/stores/permissions.store";
import { useUserStore } from "@lib/stores/user.store";
import { extractIdFromIri } from "@lib/utils/apiUtils";
import { createRootRoute, redirect } from "@tanstack/react-router";

/**
 * Route context type for TypeScript
 * This defines what will be available in context for all child routes
 */
export const routeOptions = {
  context: {
    /** Whether user is authenticated */
    isAuthenticated: false,
    /** Wildcard permission (*) grants full access */
    hasWildcard: false,
    /** Array of permission strings */
    permissions: [] /** @type {string[]} */,
    /** User context from permissions (filiale, dept, manager status) */
    permissionContext:
      null /** @type {import('@lib/permissions/types').PermissionContext | null} */,
    /** ABAC: Capabilities derived from permissions + context */
    capabilities: [] /** @type {import('@lib/permissions/types').CapabilityId[]} */,
    /** ABAC: User type derived from capabilities */
    userType: null /** @type {import('@lib/permissions/types').UserType | null} */,
    /** ABAC: Dashboard route based on capabilities */
    dashboardRoute: null /** @type {string | null} */,
  },
};

export const Route = createRootRoute({
  staticData: {},
  loaderDeps: () => ({
    userId: useAuthStore.getState().userId,
  }),
  beforeLoad: async ({ context, location }) => {
    // Rehydrate auth store first
    await useAuthStore.persist.rehydrate();
    const { isLoggedIn, userId, token } = useAuthStore.getState();
    // Public routes that don't require authentication
    // /auth/* covers /auth/login, /auth/pointage, /auth/forgot-password, /auth/setup-password
    const isPublicRoute = location.pathname.startsWith("/auth");

    // Auth redirects: redirect non-logged-in users to login
    if (!isLoggedIn && !isPublicRoute) {
      throw redirect({
        to: "/auth/login",
        search: { redirect: location.href },
      });
    }

    // Redirect logged-in users away from login page only
    if (isLoggedIn && location.pathname.startsWith("/auth/login")) {
      throw redirect({ to: "/" });
    }

    // Return context that will be merged and available to all child routes
    // This is the KEY to fixing race conditions - permissions are in context, not Zustand
    return {
      isAuthenticated: isLoggedIn,
      hasWildcard: false,
      permissions: [],
      permissionContext: null,
      capabilities: [],
      userType: null,
      dashboardRoute: null,
    };
  },
  loader: async ({ context: ctx, context: { queryClient } }) => {
    await useAuthStore.persist.rehydrate();
    await usePermissionStore.persist.rehydrate();
    const { isLoggedIn, userId, token } = useAuthStore.getState();

    if (import.meta.env?.DEV === true) {
      console.log("[RootLoader] Starting loader", {
        isLoggedIn,
        userId,
        token: token ? "has-token" : "no-token",
      });
    }

    if (!isLoggedIn || !userId) {
      if (import.meta.env?.DEV === true) {
        console.log("[RootLoader] Not logged in or no userId, returning early");
      }
      return {
        isAuthenticated: false,
        hasWildcard: false,
        permissions: [],
        permissionContext: null,
        capabilities: [],
        userType: null,
        dashboardRoute: null,
        queryClient,
      };
    }

    try {
      const [userData, { permissionsArray, context, hasWildcard: apiHasWildcard }] =
        await Promise.all([
          queryClient.ensureQueryData(currentUserQueryOptions(userId, token)),
          queryClient.ensureQueryData(permissionQueryOptions(userId, token)),
        ]);

      // Use API-provided hasWildcard if available, otherwise infer from permissions
      const hasWildcard =
        apiHasWildcard !== undefined ? apiHasWildcard : permissionsArray.includes("*");

      // Normalize context to new format
      const normalizedContext = {
        filiale_code: context.filiale_code ?? "",
        dept_path: context.dept_path ?? "",
        is_manager: context.is_manager ?? false,
        is_dept_head: context.is_dept_head ?? false,
        personne_id: String(context.personne_id ?? ""),
        managed_count: context.managed_count ?? 0,
      };

      // Extract personne ID from IRI if present (e.g., "/api/personnes/1" -> 1)
      const personneId = userData.personne ? extractIdFromIri(userData.personne) : null;

      // Update Zustand stores for UI components
      useUserStore.getState().setUser({
        ...userData,
        personneId,
      });
      usePermissionStore.getState().setPermissions({
        permissions: permissionsArray,
        hasWildcard,
        context: normalizedContext,
      });

      // Derive capabilities for debugging (also done in usePermissions hook)
      const capabilities = deriveCapabilities(permissionsArray, normalizedContext);
      const userType = determineUserType(capabilities);
      const dashboardRoute = getDashboardRoute(capabilities);

      if (import.meta.env?.DEV === true) {
        console.log(
          "%c RootLoader ",
          "padding: 2px 4px; border-radius: 3px; font-weight: bold; color: white; background: #e67e22;",
          "Populated stores",
          {
            userId,
            permissionCount: permissionsArray.length,
            hasWildcard,
            permissions: permissionsArray,
            capabilities: Array.from(capabilities),
            userType,
            dashboardRoute,
            context: normalizedContext,
          },
        );
      }

      // Return permissions in loaderData for child routes
      // Child routes access this via parentMatchPromise or directly from their loader context
      // NOTE: capabilities, userType, dashboardRoute are derived in hooks, not in loader
      return {
        isAuthenticated: true,
        hasWildcard,
        permissions: permissionsArray,
        permissionContext: normalizedContext,
        queryClient,
      };
    } catch (error) {
      console.error("[RootLoader] Failed to load auth data:", error);
      if (import.meta.env?.DEV === true) {
        console.error("[RootLoader] Error details:", {
          message: error?.message,
          stack: error?.stack,
          userId,
          hasToken: !!token,
        });
      }
      useAuthStore.getState().logout();
      throw redirect({ to: "/auth/login" });
    }
  },
  notFoundComponent: NotFound,
  component: RootLayout,
  errorComponent: ErrorComponent,
});
